import { useQuery } from "@tanstack/react-query";
import { Settings as SettingsIcon, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Separator } from "@/components/ui/Separator";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch, ApiError } from "@/lib/api";

interface CompanyProfile {
  company_name?: string;
  website?: string;
  logo_url?: string;
  address?: string;
  industry?: string;
  size?: string;
  billing_email?: string;
}

export function TenantSettings({ tenantCode }: { tenantCode: string }) {
  const { idToken } = useAuth();

  const query = useQuery<CompanyProfile, ApiError>({
    queryKey: ["company", tenantCode, idToken],
    queryFn: () =>
      apiFetch<CompanyProfile>(
        `/api/v1/tenants/${encodeURIComponent(tenantCode)}/company/profile`,
        {},
        idToken!,
      ),
    enabled: !!idToken,
    retry: false,
  });

  const comingSoon =
    query.isError && (query.error.status === 501 || query.error.code === "not_implemented");

  return (
    <div className="p-8 space-y-6 max-w-3xl">
      <header className="flex items-center gap-3">
        <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet">
          <SettingsIcon className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-4xl">Settings</h1>
          <p className="text-sm text-muted-foreground">Company profile and workspace config.</p>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Company profile</CardTitle>
          <CardDescription>Read-only for MVP — editing ships next.</CardDescription>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <div className="py-8 flex justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : comingSoon ? (
            <div className="py-8 text-sm text-muted-foreground">Settings editing coming soon.</div>
          ) : query.isError ? (
            <div className="py-8 text-sm text-signal-failed">Couldn't load: {query.error.message}</div>
          ) : (
            <dl className="space-y-3 text-sm">
              <Row label="Company" value={query.data?.company_name} />
              <Row label="Website" value={query.data?.website} mono />
              <Row label="Industry" value={query.data?.industry} />
              <Row label="Size" value={query.data?.size} />
              <Row label="Billing email" value={query.data?.billing_email} mono />
              <Row label="Address" value={query.data?.address} />
            </dl>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Workspace</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="space-y-3 text-sm">
            <Row label="Subdomain" value={`${tenantCode}.virtualdesk.ai`} mono />
            <Separator />
            <Row label="Client code" value={tenantCode.toUpperCase()} mono />
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value?: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-xs text-right" : "text-right"}>{value || "—"}</dd>
    </div>
  );
}
